// src/components/Header.jsx
import { NavLink, useNavigate } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';
import { useState, useEffect } from 'react';
import { FiMenu, FiX } from 'react-icons/fi';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';

const navItems = [
  { name: 'Work', path: '/portfolio' },
  { name: 'Services', path: '/services' },
  { name: 'Blog', path: '/blog' },
  { name: 'Subscription', path: '/subscription' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

function Header() {
  const { resolvedTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [adminProfile, setAdminProfile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch admin profile directly from Firestore
  useEffect(() => {
    const fetchAdminProfile = async () => {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('isAdmin', '==', true));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const adminDoc = querySnapshot.docs[0];
          const data = adminDoc.data();
          setAdminProfile({ id: adminDoc.id, ...data });

          console.log('Admin profile loaded:', {
            username: data.username,
            fullName: data.fullName,
          });
        }
      } catch (error) {
        console.error('Error fetching admin profile:', error);
      }
    };

    fetchAdminProfile();
  }, []);

  const handleProfileClick = () => {
    if (!adminProfile) return;

    let username = adminProfile.username;

    // Remove @ if present at the beginning
    if (username && username.startsWith('@')) {
      username = username.substring(1);
    }

    console.log('Navigating to profile:', username);
    if (username) {
      navigate(`/${username}`);
    }
  };

  const getInitials = () => {
    if (adminProfile?.fullName) {
      return adminProfile.fullName.charAt(0).toUpperCase();
    }
    return 'A';
  };

  return (
    <>
      <header
        className="fixed top-4 left-1/2 -translate-x-1/2 w-[calc(100%-1.5rem)] max-w-7xl backdrop-blur-sm opacity-90 z-50 transition-all duration-300 rounded-2xl overflow-visible"
        style={{
          backgroundColor: scrolled ? 'var(--bg)' : 'var(--bg)',
          backgroundBlendMode: 'overlay',
          border: '1px solid var(--border)',
          boxShadow: scrolled ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none',
        }}
      >
        <div className="px-3 sm:px-4 md:px-6 h-14 md:h-16 flex items-center justify-between rounded-2xl">
          {/* Logo */}
          <NavLink to="/" className="flex items-center shrink-0">
            <img
              src={
                resolvedTheme === 'dark'
                  ? 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1784030465/wd-hejbul-official-brand-logo-white-version_xvqyeo.png'
                  : 'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1784030465/wd-hejbul-official-brand-logo-black-version_gaippc.png'
              }
              alt="WD Hajbul Official Brand Logo"
              className="h-7 sm:h-8 md:h-10 w-auto object-contain transition-all duration-300"
            />
          </NavLink>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-4 lg:gap-8">
            {navItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-all duration-200 ${
                    isActive ? 'font-medium' : 'opacity-60 hover:opacity-100'
                  }`
                }
                style={{ color: 'var(--text)' }}
              >
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Theme Toggle - Desktop */}
            <div className="hidden md:block">
              <ThemeToggle />
            </div>

            {/* Hire Me Button */}
            <NavLink
              to="/hire"
              className="hidden md:inline-block px-3 md:px-5 py-1.5 md:py-2 text-sm rounded-full transition-all duration-300 hover:scale-105"
              style={{
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = 'var(--text)';
                e.currentTarget.style.color = 'var(--bg)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--text)';
              }}
            >
              Hire Me
            </NavLink>

            {/* Admin Profile Icon */}
            {adminProfile && (
              <button
                onClick={handleProfileClick}
                className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-full transition-all duration-300 hover:scale-110 hover:ring-2 focus:outline-none cursor-pointer"
                style={{
                  backgroundColor: 'var(--border)',
                  border: '2px solid var(--text)',
                }}
                title="View Public Profile"
              >
                {adminProfile?.profilePictureURL ? (
                  <img
                    src={adminProfile.profilePictureURL}
                    alt="Profile"
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <span
                    className="text-sm font-bold"
                    style={{ color: 'var(--text)' }}
                  >
                    {getInitials()}
                  </span>
                )}
              </button>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-full transition-colors hover:bg-black/5 dark:hover:bg-white/5"
              style={{ color: 'var(--text)' }}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <FiX className="w-5 h-5" />
              ) : (
                <FiMenu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-x-4 top-[72px] z-40 md:hidden transition-all duration-300 transform ${
          mobileMenuOpen
            ? 'opacity-100 translate-y-0 visible'
            : 'opacity-0 -translate-y-4 invisible'
        }`}
      >
        <div
          className="backdrop-blur-xl rounded-2xl shadow-xl p-4"
          style={{
            backgroundColor: 'var(--bg)',
            border: '1px solid var(--border)',
          }}
        >
          <nav className="flex flex-col gap-2">
            {navItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-base transition-all duration-200 ${
                    isActive ? 'opacity-100 font-medium' : 'opacity-60'
                  }`
                }
                style={{ color: 'var(--text)' }}
              >
                {item.name}
              </NavLink>
            ))}

            {/* Mobile Menu Divider */}
            <div
              className="flex items-center justify-between mt-2 pt-2"
              style={{ borderTop: '1px solid var(--border)' }}
            >
              <ThemeToggle />
              <NavLink
                to="/hire"
                onClick={() => setMobileMenuOpen(false)}
                className="px-5 py-2 text-sm rounded-full transition-all duration-300 hover:scale-105"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                Hire Me
              </NavLink>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}

export default Header;
